import { z } from 'zod'
import type { ProgressView } from './progress/xp'

export const ROOM_CODE_LENGTH = 8
/** Unambiguous alphabet: no 0/O/1/I. */
export const ROOM_CODE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ'

/** A room with no game-state write in this long is deleted; any game's mutation resets it. */
export const ROOM_TTL_MS = 15 * 60 * 1000

export const NICKNAME_MIN = 2
export const NICKNAME_MAX = 20
export const nicknameSchema = z
  .string()
  .trim()
  .min(NICKNAME_MIN, `Nickname must be at least ${NICKNAME_MIN} characters`)
  .max(NICKNAME_MAX, `Nickname must be at most ${NICKNAME_MAX} characters`)
  .regex(/^[\p{L}\p{N} _.-]+$/u, 'Only letters, numbers, spaces, _ . - are allowed')

export const roomCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .length(ROOM_CODE_LENGTH)
  .regex(new RegExp(`^[${ROOM_CODE_ALPHABET}]+$`))

// ---- profile flair ----

export const AVATARS = [
  '🦊',
  '🐼',
  '🐸',
  '🐙',
  '🦄',
  '🐯',
  '🐧',
  '🦁',
  '🐨',
  '🐵',
  '🦖',
  '🐳',
  '🍕',
  '🚀',
  '⚡',
  '🎲',
  '🤖',
] as const
export type Avatar = (typeof AVATARS)[number]
export const avatarSchema = z.enum(AVATARS)

export const COLORS = [
  'rose',
  'orange',
  'amber',
  'lime',
  'emerald',
  'sky',
  'violet',
  'pink',
] as const
export type Color = (typeof COLORS)[number]
export const colorSchema = z.enum(COLORS)

export const profilePatchSchema = z
  .object({
    nickname: nicknameSchema.optional(),
    avatar: avatarSchema.optional(),
    color: colorSchema.optional(),
    /** Hide from the online list and opt out of challenges. */
    appearOffline: z.boolean().optional(),
  })
  .refine((p) => Object.values(p).some((v) => v !== undefined), { message: 'Nothing to update' })
export type ProfilePatch = z.infer<typeof profilePatchSchema>

export interface Flair {
  avatar: Avatar
  color: Color
}

/** Deterministic flair for players who never picked one, so old rows still look consistent. */
export function defaultFlair(playerId: string): Flair {
  let h = 2166136261
  for (let i = 0; i < playerId.length; i++) h = (h ^ playerId.charCodeAt(i)) * 16777619
  h >>>= 0
  return {
    avatar: AVATARS[h % AVATARS.length]!,
    color: COLORS[(h >>> 8) % COLORS.length]!,
  }
}

export interface PlayerPublic {
  id: string
  nickname: string
  avatar?: Avatar
  color?: Color
}

export function flairOf(p: { id: string; avatar?: Avatar | null; color?: Color | null }): Flair {
  const d = defaultFlair(p.id)
  return { avatar: p.avatar ?? d.avatar, color: p.color ?? d.color }
}

// ---- reactions ----

/**
 * Reactions travel as ids; each client renders them (emoji for XOX, drawn stickers for Ice).
 * The server accepts the whole list so a spectator can use either game's set.
 */
export const REACTION_IDS = [
  'lol',
  'fire',
  'wow',
  'clap',
  'cry',
  'think',
  'snow',
  'chill',
  'roar',
  'gg',
  'flag',
  'splash',
] as const
export type Reaction = (typeof REACTION_IDS)[number]
export const reactionSchema = z.enum(REACTION_IDS)
export const REACTION_SETS = {
  xox: ['lol', 'fire', 'wow', 'clap', 'cry', 'think'],
  ice: ['snow', 'chill', 'roar', 'gg', 'flag', 'splash'],
} as const satisfies Record<'xox' | 'ice', readonly Reaction[]>

/** Seat/spectator role for the reaction bubble system; each game maps its own seats onto this. */
export type Role = 'X' | 'O' | 'spectator'

// ---- client -> server ----

/** Frames every game's client protocol includes; the WebSocket endpoint handles them itself. */
export const COMMON_CLIENT_MESSAGES = [
  z.object({ type: z.literal('react'), emoji: reactionSchema }),
  z.object({ type: z.literal('ping'), t: z.number() }),
] as const

// ---- server -> client ----

/**
 * Codes the generic transport/room infra can raise for any game, before game-specific handling:
 * the three the WebSocket endpoint itself emits, plus `STALE`, which `casMutate`
 * (`apps/server/src/rooms/mutate.ts`) can return from its own compare-and-set retry logic
 * regardless of which game's mutation it's running.
 */
export const ERROR_CODES = ['RATE_LIMITED', 'ROOM_NOT_FOUND', 'BAD_MESSAGE', 'STALE'] as const
export type ErrorCode = (typeof ERROR_CODES)[number]

export const ERROR_TEXT: Record<ErrorCode, string> = {
  RATE_LIMITED: 'Slow down',
  ROOM_NOT_FOUND: 'This room no longer exists',
  BAD_MESSAGE: 'Malformed message',
  STALE: 'Out of date — try again',
}

export interface ReactionEvent {
  id: string
  emoji: Reaction
  from: { id: string; nickname: string } & Flair
  role: Role
  at: number
}

/** Frames the WebSocket endpoint emits for every game; each game adds its own on top. */
export type CommonServerMessage<E extends string = ErrorCode> =
  | {
      type: 'welcome'
      you: { playerId: string; nickname: string }
      serverTime: number
    }
  | ({ type: 'reaction' } & ReactionEvent)
  | { type: 'error'; code: E; message: string }
  | { type: 'pong'; t: number; serverTime: number }
  | { type: 'roomClosed'; reason: 'expired' | 'restart' | 'closed' }

// ---- HTTP payloads ----

/** A one-time code that moves this guest identity to another device. */
export interface TransferCodeResponse {
  code: string
  expiresAt: number
  serverNow: number
}

export interface MeResponse {
  playerId: string
  /** Always set: a brand-new player gets a generated one (see `names.ts`). */
  nickname: string
  /** True until the player picks their own name. */
  nicknameAuto: boolean
  /** Hidden from the online list; can neither send nor receive challenges. */
  appearOffline: boolean
  avatar: Avatar
  color: Color
  /** Account level, XP and streak (fed by every game). */
  progress: ProgressView
}

export interface CreateRoomResponse {
  code: string
}
